<template>
  <div
    class="c-graph c-graph--tree"
    :class="{
      'is-error': isError,
      'is-loading': suggest.isLoading || isDrawing
    }"
    :style="{ height: size.viewH + 'px' }"
    ref="canvas"
  >
    <LoadingChara v-if="suggest.isLoading">
      <template slot="title">
        データ取得中
      </template>
    </LoadingChara>
    <Loading v-else-if="isDrawing" />
    <Error v-if="isError">
      <template slot="title">
        データがありません。
      </template>
    </Error>
    <svg :height="size.viewH" class="c-graph__svg" ref="svg">
      <g ref="gView">
        <g
          class="c-graph--tree-volume"
          :opacity="suggest.isLoading || isDrawing ? 0 : 1"
          :transform="
            'translate(' + size.viewW / 2 + ', ' + size.offset.top + ')'
          "
        >
          <rect
            :x="-size.volumeW / 2"
            y="0"
            :width="size.volumeW"
            :height="size.volumeH"
            class="c-graph--tree-volume__box"
          />
          <text
            x="0"
            y="25"
            text-anchor="middle"
            class="c-graph--tree-volume__label"
            ref="gVolumeLabel"
          >
            検索ボリューム {{ volumeDate }}
          </text>
          <text
            x="0"
            y="54"
            text-anchor="middle"
            class="c-graph--tree-volume__value"
            ref="gVolumeValue"
          >
            {{ volumeValue | separateNumber | nullToHyphen }}
          </text>
        </g>
        <g
          class="c-graph-trees"
          :transform="
            'translate(' +
              (size.viewW - (size.ancestorW - 2)) / 2 +
              ', ' +
              (size.offset.top * 2 + size.volumeH) +
              ')'
          "
          ref="gTrees"
        >
          <g
            class="c-graph-trees__left"
            :transform="'translate(' + (size.ancestorW - 1) + ', 0)'"
          >
            <g ref="gLinkLeft"></g>
            <g ref="gNodeLeft"></g>
          </g>
          <g class="c-graph-trees__right">
            <g ref="gLinkRight"></g>
            <g ref="gNodeRight"></g>
          </g>
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
import Error from '@/components/Error';
import Loading from '@/components/Loading';
import LoadingChara from '@/components/LoadingChara';
import utils from '@/utils';
import * as d3 from 'd3';

export default {
  props: ['period', 'suggest'],

  components: {
    Error,
    Loading,
    LoadingChara
  },

  data() {
    return {
      d3: {
        root: {
          left: {},
          right: {}
        },
        tree: {
          left: () => {},
          right: () => {}
        },
        zoom: () => {}
      },
      el: {
        svg: '',
        gLink: {
          left: '',
          right: ''
        },
        gNode: {
          left: '',
          right: ''
        },
        gTrees: '',
        gView: ''
      },
      size: {
        ancestorW: 0,
        labelH: [34, 30, 26],
        labelP: 10,
        labelR: 5,
        offset: {
          bottom: 32,
          left: 30,
          right: 30,
          top: 32
        },
        spacerX: 3,
        spacerY: 52,
        viewW: 0,
        viewH: 0,
        volumeMinW: 220,
        volumeW: 0,
        volumeH: 70,
        windowW: window.innerWidth
      },
      transition: {
        duration: 300
      },
      tree: {
        left: [],
        right: []
      },
      zoom: {
        x: 0,
        y: 0,
        k: 1,
        isTop: false
      },
      isDrawed: false,
      isDrawing: false
    };
  },

  computed: {
    volumeDate() {
      let date = '';
      if (this.period.length) {
        let startDate = this.$options.filters.dateText(
          this.period[0],
          'YYYY.MM'
        );
        let endDate = this.$options.filters.dateText(this.period[1], 'YYYY.MM');
        date = `(${startDate}～${endDate})`;
      }
      return date;
    },
    volumeValue() {
      let value = null;
      if (this.suggest.data && !utils.isEmpty(this.suggest.data.searchVolume)) {
        value = this.suggest.data.searchVolume;
      }
      return value;
    },
    isError() {
      if (!this.suggest.isLoading && !this.suggest.data) {
        return true;
      }
      return false;
    }
  },

  watch: {
    suggest: {
      handler() {
        this.update();
      },
      deep: true
    }
  },

  mounted() {
    this.setEl();
    this.setSize();
    this.setVolumeSize();
    this.update();
    window.addEventListener('resize', this.resize);
  },
  destroyed() {
    window.removeEventListener('resize', this.resize);
  },

  methods: {
    update() {
      if (this.suggest.isLoading || this.isError) {
        return;
      }
      this.isDrawing = true;
      this.setVolumeSize();
      this.setData();
      this.draw();
      this.setScale();
      this.isDrawed = true;
      this.isDrawing = false;
    },

    setEl() {
      this.el.svg = d3.select(this.$refs.svg);
      this.el.gLink.left = d3.select(this.$refs.gLinkLeft);
      this.el.gLink.right = d3.select(this.$refs.gLinkRight);
      this.el.gNode.left = d3.select(this.$refs.gNodeLeft);
      this.el.gNode.right = d3.select(this.$refs.gNodeRight);
      this.el.gTrees = d3.select(this.$refs.gTrees);
      this.el.gView = d3.select(this.$refs.gView);
    },

    setData() {
      let total = this.suggest.data.children.length;
      let half = Math.ceil(total / 2);
      this.tree.left = {
        name: this.suggest.data.name,
        children: this.suggest.data.children.slice(0, half)
      };
      this.tree.right = {
        name: this.suggest.data.name,
        children: this.suggest.data.children.slice(half, total)
      };
      this.d3.root.left = d3.hierarchy(this.tree.left).count();
      this.d3.root.right = d3.hierarchy(this.tree.right).count();
      this.d3.tree.left = d3.tree(this.d3.root.left);
      this.d3.tree.right = d3.tree(this.d3.root.right);
    },

    setSize() {
      this.size.viewW = this.$refs.canvas.clientWidth;
    },

    setVolumeSize() {
      if (!this.$refs.gVolumeLabel || !this.$refs.gVolumeValue) {
        return;
      }
      let volumeLabelW = this.$refs.gVolumeLabel.getBBox().width;
      let volumeValueW = this.$refs.gVolumeValue.getBBox().width;
      let maxW = Math.max(volumeLabelW, volumeValueW, this.size.volumeMinW);
      this.size.volumeW = Math.round(maxW + this.size.labelP * 2);
    },

    setScale() {
      // 現在のズームの状態を保存する
      let zoom = Object.assign({}, this.zoom);
      if (this.isDrawed) {
        // 初期状態のサイズを取得したいので、一瞬だけズームを戻す
        this.zoomReset(0);
      }
      this.size.viewH =
        this.el.gTrees.node().getBBox().height +
        this.size.offset.top * 2 +
        this.size.volumeH +
        this.size.offset.bottom;

      // extentを指定しないとzoom時のx座標がややずれる
      this.d3.zoom = d3
        .zoom()
        .extent([
          [0, 0],
          [this.size.viewW, this.size.viewH]
        ])
        .duration(this.transition.duration)
        .on('start', () => {
          this.zoom.isTop = false;
          this.el.svg.classed('is-drag', true);
        })
        .on('zoom', this.zoomed)
        .on('end', () => {
          this.el.svg.classed('is-drag', false);
        });

      this.el.svg.call(this.d3.zoom).on('wheel.zoom', null);

      if (this.isDrawed) {
        // 元のズーム状態に戻す
        this.el.svg.call(
          this.d3.zoom.transform,
          d3.zoomIdentity.translate(zoom.x, zoom.y).scale(zoom.k)
        );
      }
    },

    draw() {
      this.drawTree('left');
      this.drawTree('right');
    },

    drawTree(orientation) {
      this.el.gNode[orientation]
        .selectAll('g')
        .data(this.d3.root[orientation].descendants())
        .join(
          enter => {
            return enter
              .append('g')
              .attr('opacity', d => {
                return !d.parent && orientation === 'right' ? 0 : 1;
              })
              .call(enter => {
                enter.append('rect').call(this.attrLabel);
              })
              .call(enter => {
                enter.append('text').call(this.attrText);
              })
              .call(enter => enter.append('circle').call(this.attrCircle));
          },
          update => {
            return update
              .call(update => update.select('rect').call(this.attrLabel))
              .call(update => update.select('text').call(this.attrText))
              .call(update => update.select('circle').call(this.attrCircle));
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, i, nodes) => {
          let el = d3.select(nodes[i]);
          let pos = this.getPos(d, orientation);
          d.index = pos.index;
          d.x = pos.x + d.depth * 2;
          d.y = pos.y;

          if (i === 0) {
            this.size.ancestorW = d.width;
          }
          el.attr('transform', d => {
            let x = d.y;
            let y = d.x + this.size.labelH[d.depth] / 2;
            if (orientation === 'left') {
              x = -x;
            }
            return 'translate(' + x + ',' + y + ')';
          });
          el.select('text').attr('x', () => {
            if (orientation === 'left') {
              return -d.width + this.size.labelP;
            } else {
              return this.size.labelP;
            }
          });
          el.select('rect')
            .attr('width', d.width)
            .attr('x', () => {
              return orientation === 'left' ? -d.width : 0;
            });
        });

      this.el.gLink[orientation]
        .selectAll('path')
        .data(this.d3.root[orientation].links())
        .join(
          enter => {
            return enter.append('path').call(this.attrLink);
          },
          update => {
            return update.call(this.attrLink);
          },
          exit => {
            return exit.remove();
          }
        )
        .each((d, i, nodes) => {
          let el = d3.select(nodes[i]);
          let path = '';
          // 1番上の枝は直線
          if (d.target.index === 0) {
            // prettier-ignore
            path = `M${d.source.y + d.source.width},${d.target.x} L${d.target.y},${d.target.x}`;
          } else {
            let prevX = d3.select(nodes[i - 1]).datum().target.x;
            // 2番目の枝は曲線から始まる
            if (d.target.index === 1) {
              // prettier-ignore
              path = `M${d.source.y + d.source.width},${prevX} L${d.source.y + d.source.width + 20 - 5},${prevX} Q${d.source.y + d.source.width + 20},${prevX} ${d.source.y + d.source.width + 20},${prevX + 5}`;
            } else {
              // prettier-ignore
              path = `M${d.source.y + d.source.width + 20},${prevX - 10}`;
            }
            // prettier-ignore
            path += `L${d.source.y + d.source.width + 20},${d.target.x - 10} Q${d.source.y + d.source.width + 20},${d.target.x} ${d.source.y + d.source.width + 20 + 10},${d.target.x} L${d.target.y},${d.target.x}`;
          }

          el.attr('d', path).attr('transform', d => {
            let transform =
              'translate(0,' + this.size.labelH[d.target.depth] / 2 + ')';
            if (orientation === 'left') {
              transform += ' scale(-1, 1)';
            }
            return transform;
          });
        });
    },

    attrLabel(selection) {
      selection
        .attr('class', d => {
          return 'c-graph-trees__label is-d' + d.depth;
        })
        .attr('x', 0)
        .attr('y', d => {
          return -(this.size.labelH[d.depth] / 2);
        })
        .attr('height', d => {
          return this.size.labelH[d.depth];
        })
        .attr('rx', this.size.labelR)
        .attr('ry', this.size.labelR);
    },

    attrText(selection) {
      selection
        .attr('class', d => {
          return 'c-graph-trees__text is-d' + d.depth;
        })
        .attr('dy', d => {
          return 7 - d.depth;
        })
        .attr('x', this.size.labelP)
        .text(d => {
          return d.data.name;
        })
        .each((d, i, nodes) => {
          if (!utils.isEmpty(d.data.searchVolume)) {
            let el = d3.select(nodes[i]);
            el.append('tspan')
              .text(
                '　' + this.$options.filters.separateNumber(d.data.searchVolume)
              )
              .attr('dy', d => {
                return d.depth < 3 ? -1 : 0;
              });
          }
          d.width = Math.round(
            nodes[i].getComputedTextLength() + this.size.labelP * 2
          );
        });
    },

    attrCircle(selection) {
      selection
        .attr('class', 'c-graph-trees__circle')
        .attr('r', 4)
        .attr('opacity', d => {
          // 始祖は○を表示しない
          return d.parent ? 1 : 0;
        });
    },

    attrLink(selection) {
      selection.attr('class', 'c-graph-trees__link');
    },

    getPos(d, orientation) {
      let pos = {
        index: 0,
        x: 0,
        y: 0
      };
      let routes = this.d3.root[orientation].path(d);

      for (let i = 0; i < routes.length; i++) {
        let route = routes[i];
        let siblings = this.d3.root[orientation].descendants().filter(item => {
          return item.depth === route.depth && item.parent === route.parent;
        });
        pos.index = siblings.findIndex(item => {
          return item.data.name == route.data.name;
        });
        let olderSiblings = siblings.filter((item, index) => {
          return index < pos.index;
        });
        for (let j = 0; j < olderSiblings.length; j++) {
          let olderSibling = olderSiblings[j];
          pos.x +=
            olderSibling.value * (this.size.labelH[1] + this.size.spacerX);
          if (olderSibling.depth < 2) {
            pos.x += olderSibling.value > 1 ? 17 : 19;
          }
        }
      }
      for (let i = 0; i < routes.length - 1; i++) {
        let route = routes[i];
        pos.y += route.width + this.size.spacerY;
      }
      return pos;
    },

    zoomed() {
      if (this.zoom.isTop) {
        d3.event.transform.y = 0;
      }
      this.zoom.x = d3.event.transform.x;
      this.zoom.y = d3.event.transform.y;
      this.zoom.k = d3.event.transform.k;
      this.el.gView.attr('transform', d3.event.transform);

      this.$emit('zoomed', this.zoom);
    },
    zoomByScale(step) {
      this.el.svg
        .interrupt()
        .transition()
        .duration(this.transition.duration)
        .call(this.d3.zoom.scaleBy, step)
        .on('start', () => {
          // yが0の状態でズームイン/アウトした時は、y座標を動かさない
          this.zoom.isTop = this.zoom.y === 0;
        });
    },
    zoomIn() {
      this.zoomByScale(1.25);
    },
    zoomOut() {
      this.zoomByScale(0.8);
    },
    zoomReset(transition = this.transition.duration) {
      if (transition) {
        this.el.svg
          .interrupt()
          .transition()
          .duration(this.transition.duration)
          .call(this.d3.zoom.transform, d3.zoomIdentity);
      } else {
        this.el.svg.call(this.d3.zoom.transform, d3.zoomIdentity);
      }
    },

    resize() {
      this.setSize();
      if (!this.isDrawed) {
        return;
      }
      if (this.size.windowW !== window.innerWidth) {
        this.size.windowW = window.innerWidth;
        this.zoomReset(0);
        this.setScale();
      }
    }
  }
};
</script>
